import {AvatarStack} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {Thread, Author} from '@github-ui/diff-lines/conversation-types'
import {useMemo} from 'react'

type Props = {
  skipBeforeCommentId?: number | null
  skipAfterCommentId?: number | null
  thread: Thread
}

export function PreviewAuthors({skipBeforeCommentId, skipAfterCommentId, thread}: Props) {
  const {commentAuthors} = useMemo(() => {
    const uniqueAuthors = new Set<string>()
    const commentsArray = thread?.commentsData?.comments ?? []

    // Start collecting immediately if no skipBefore constraint
    let shouldCollect = !skipBeforeCommentId

    const extractedAuthors = commentsArray.reduce<Author[]>((authors, comment) => {
      // Check if we should start collecting (passed the skipBefore comment)
      if (skipBeforeCommentId && comment.databaseId === skipBeforeCommentId) {
        // eslint-disable-next-line react-hooks/immutability
        shouldCollect = true
        return authors // skip this comment itself
      }

      // Check if we should stop collecting (reached the skipAfter comment)
      if (skipAfterCommentId && comment.databaseId === skipAfterCommentId) {
        shouldCollect = false
      }

      if (!shouldCollect) return authors

      const author = comment.author
      if (!author || !author.avatarUrl) return authors

      if (!uniqueAuthors.has(author.avatarUrl)) {
        authors.push(author)
        uniqueAuthors.add(author.avatarUrl)
      }

      return authors
    }, [])

    return {commentAuthors: extractedAuthors}
  }, [skipBeforeCommentId, skipAfterCommentId, thread?.commentsData?.comments])

  // If no authors and no label, render nothing
  if (commentAuthors.length < 1) return null

  return (
    <AvatarStack>
      {commentAuthors.map(({login, avatarUrl}) => (
        <GitHubAvatar key={avatarUrl} alt={login} size={18} src={avatarUrl} />
      ))}
    </AvatarStack>
  )
}
